import type { FileStatusId } from '@steerfi/shared';
import { FILE_STATUS, PAYMENT_STATUS, ROW_PROCESSING_STATUS } from '@steerfi/shared';
import {
  AlertTriangle,
  Calculator,
  Calendar,
  Download,
  FileText,
  Landmark,
  Receipt,
  Wallet,
} from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { getDownloadUrl, getPaymentFileById, type PaymentFileItem } from '@/api/file-upload-api';
import { getFileEventLog, getPaymentsByFileId, type PaymentWithRelations } from '@/api/payments';
import { getSupplierAddConfig } from '@/api/suppliers';
import {
  DetailInfoCell,
  DetailMetricCard,
  DetailSectionHeader,
} from '@/components/detail-page-primitives';
import { EventTimeline } from '@/components/event-timeline';
import { PaymentDetails } from '@/components/payment-details';
import { PaymentFileDetailsTable } from '@/components/payment-file-details-table';
import { getStatusColor, getStatusLabel } from '@/components/status-chip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useDocumentTitle } from '@/contexts/document-title-context';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { useFocusReturn } from '@/hooks/use-focus-return';
import { usePaymentFileActions } from '@/hooks/use-payment-file-actions';
import { formatDateLong } from '@/lib/utils';

function formatCurrency(amount: number | string | null): string {
  const amountNum = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (!amountNum) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amountNum);
}

function parseAmount(amount: number | string | null | undefined): number {
  if (typeof amount === 'number') return Number.isFinite(amount) ? amount : 0;
  if (!amount) return 0;
  const parsed = parseFloat(String(amount).replace(/[$,]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatFileSize(bytes: string | null): string {
  if (!bytes) return '—';
  const n = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
  if (!Number.isFinite(n) || n <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = n;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const rounded = unitIndex === 0 ? size : Math.round(size * 10) / 10;
  return `${rounded} ${units[unitIndex]}`;
}

/**
 * Payment files are shown in multiple places (the Payment Files page and the
 * Escrow "Collections" list, since a collection row is itself a payment file).
 * The origin is passed via router `state.from` so the breadcrumb can point back
 * to wherever the user actually came from, defaulting to Payment Files.
 */
const BREADCRUMB_ORIGINS = {
  escrow: { label: 'Escrow', to: '/escrow' },
  'payment-files': { label: 'Payment Files', to: '/payment-files' },
} as const;

type BreadcrumbOrigin = keyof typeof BREADCRUMB_ORIGINS;

function resolveBreadcrumbOrigin(from: unknown): (typeof BREADCRUMB_ORIGINS)[BreadcrumbOrigin] {
  if (typeof from === 'string' && from in BREADCRUMB_ORIGINS) {
    return BREADCRUMB_ORIGINS[from as BreadcrumbOrigin];
  }
  return BREADCRUMB_ORIGINS['payment-files'];
}

interface MethodBreakdownEntry {
  name: string;
  count: number;
  amount: number;
}

/** Per payment-method totals (count + summed amount), excluding failed stub payments. */
function buildMethodBreakdown(payments: PaymentWithRelations[]): MethodBreakdownEntry[] {
  const map = new Map<string, MethodBreakdownEntry>();
  for (const payment of payments) {
    if (payment.status_id === PAYMENT_STATUS.FAILED) continue;
    const name = payment.payment_method?.name || 'Unknown';
    const amountNum =
      typeof payment.amount_total === 'string'
        ? parseFloat(payment.amount_total)
        : (payment.amount_total ?? 0);
    const entry = map.get(name) ?? { name, count: 0, amount: 0 };
    entry.count += 1;
    entry.amount += Number.isFinite(amountNum) ? amountNum : 0;
    map.set(name, entry);
  }
  return Array.from(map.values());
}

const BREAKDOWN_BAR_COLORS = [
  'bg-blue-500',
  'bg-amber-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-rose-500',
  'bg-cyan-500',
];

function methodIcon(name: string): React.ElementType {
  return /check/i.test(name) ? Receipt : Landmark;
}

function PaymentMethodBreakdownCard({ payments }: { payments: PaymentWithRelations[] }) {
  const methods = useMemo(() => buildMethodBreakdown(payments), [payments]);
  if (methods.length === 0) return null;

  const totalCount = methods.reduce((sum, m) => sum + m.count, 0);
  const totalAmount = methods.reduce((sum, m) => sum + m.amount, 0);

  return (
    <Card className="py-0">
      <CardContent className="p-0">
        <DetailSectionHeader
          icon={Receipt}
          title="Payment Method Breakdown"
          meta={`${methods.length} ${methods.length === 1 ? 'method' : 'methods'}`}
        />
        <div className="p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {methods.map((method, index) => {
              const pct = totalCount > 0 ? Math.round((method.count / totalCount) * 100) : 0;
              const Icon = methodIcon(method.name);
              const barColor = BREAKDOWN_BAR_COLORS[index % BREAKDOWN_BAR_COLORS.length];
              return (
                <div key={method.name} className="rounded-lg border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {method.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {method.count} {method.count === 1 ? 'payment' : 'payments'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold tabular-nums text-foreground">
                        {formatCurrency(method.amount)}
                      </p>
                      <p className="text-xs text-muted-foreground">{pct}%</p>
                    </div>
                  </div>
                  <div className="mt-2.5 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded-full ${barColor}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-3 pt-3 border-t">
            <span className="text-xs text-muted-foreground">Total across methods</span>
            <span className="text-sm font-medium font-mono tabular-nums text-foreground">
              {formatCurrency(totalAmount)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export const PaymentFileDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { user } = useKeycloakAuth();

  const breadcrumbOrigin = resolveBreadcrumbOrigin(
    (location.state as { from?: unknown } | null)?.from
  );

  const [file, setFile] = useState<PaymentFileItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [payments, setPayments] = useState<PaymentWithRelations[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);

  const [externalRefLabel, setExternalRefLabel] = useState<string>('External Ref');

  const [selectedPayment, setSelectedPayment] = useState<PaymentWithRelations | null>(null);
  const { capture: captureFocus, restore: restoreFocus } = useFocusReturn();
  const [isPaymentDetailsOpen, setIsPaymentDetailsOpen] = useState(false);

  const fetchFile = useCallback(async () => {
    if (!id) {
      setIsLoading(false);
      setNotFound(true);
      return;
    }
    setIsLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const data = await getPaymentFileById(id);
      setFile(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load payment file';
      if (/not found/i.test(message)) {
        setNotFound(true);
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const fetchPayments = useCallback(async () => {
    if (!id) return;
    setIsLoadingPayments(true);
    setPaymentsError(null);
    try {
      const response = await getPaymentsByFileId(id, {
        page: 1,
        limit: 100,
        sort_by: 'created_at',
        sort_order: 'DESC',
      });
      setPayments(response.data || []);
    } catch (err) {
      setPaymentsError(err instanceof Error ? err.message : 'Failed to load payments');
    } finally {
      setIsLoadingPayments(false);
    }
  }, [id]);

  useEffect(() => {
    fetchFile();
    fetchPayments();
  }, [fetchFile, fetchPayments]);

  // Only call add-config once the file has loaded so we use the file's own
  // party_id rather than firing twice (first with the user fallback, then again
  // when party_id resolves from the file response).
  useEffect(() => {
    const partyId = file?.party_id ?? user?.partyId;
    if (isLoading || !partyId) return;
    getSupplierAddConfig(partyId)
      .then((config) => {
        setExternalRefLabel(config.externalRefDisplayName?.trim() || 'External Ref');
      })
      .catch(() => {});
  }, [file?.party_id, user?.partyId, isLoading]);

  const handleActionSuccess = useCallback(() => {
    fetchFile();
    fetchPayments();
  }, [fetchFile, fetchPayments]);

  const { isProcessing, actionButtons, showDownloadButton } = usePaymentFileActions({
    paymentFile: file,
    onActionSuccess: handleActionSuccess,
  });

  const handleDownload = useCallback(async () => {
    if (!file) return;
    try {
      const downloadData = await getDownloadUrl(file.file_id);
      window.open(downloadData.signed_url, '_blank');
      toast.success(`Downloading ${file.file_name}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to download file');
    }
  }, [file]);

  const handlePaymentClick = (payment: PaymentWithRelations) => {
    captureFocus();
    setSelectedPayment(payment);
    setIsPaymentDetailsOpen(true);
  };

  const sortedRowResults = useMemo(() => {
    if (!file?.row_results) return null;
    return [...file.row_results].sort((a, b) => a.row_number - b.row_number);
  }, [file?.row_results]);

  const rowSummary = useMemo(() => {
    const totalRows = sortedRowResults?.length ?? 0;
    const failedRows =
      sortedRowResults?.filter((row) => row.status === ROW_PROCESSING_STATUS.FAILED).length ?? 0;
    const successfulRows = Math.max(totalRows - failedRows, 0);
    const parsedTotal = sortedRowResults?.reduce((sum, row) => {
      const amount = row.metadata?.amount;
      return (
        sum + parseAmount(typeof amount === 'string' || typeof amount === 'number' ? amount : null)
      );
    }, 0);

    return {
      totalRows,
      failedRows,
      successfulRows,
      parsedTotal: parsedTotal ?? 0,
      hasRowResults: totalRows > 0,
    };
  }, [sortedRowResults]);

  useDocumentTitle(file?.file_name);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <p className="text-sm text-muted-foreground">Loading payment file…</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <p className="text-sm text-muted-foreground">Payment file not found.</p>
        <Link to={breadcrumbOrigin.to} className="text-sm underline underline-offset-4">
          Back to {breadcrumbOrigin.label}
        </Link>
      </div>
    );
  }

  if (error || !file) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive text-sm">
          {error ?? 'Failed to load payment file.'}
        </div>
        <Link to={breadcrumbOrigin.to} className="text-sm underline underline-offset-4">
          Back to {breadcrumbOrigin.label}
        </Link>
      </div>
    );
  }

  const totalAmountNum = parseAmount(file.total_payment_amount);
  const createdPaymentCount = file.payment_count ?? payments.length;
  const avgAmount = createdPaymentCount ? totalAmountNum / createdPaymentCount : 0;
  const fileStatus = file.status_type_id as FileStatusId;
  const statusVariant = getStatusColor(fileStatus, 'file');
  const statusLabel = getStatusLabel(fileStatus, 'file');
  const isFailedLike = fileStatus === FILE_STATUS.FAILED || fileStatus === FILE_STATUS.NEEDS_REVIEW;
  const validationTone = fileStatus === FILE_STATUS.FAILED ? 'destructive' : 'warning';
  const rowCountLabel = rowSummary.hasRowResults
    ? `${rowSummary.totalRows} ${rowSummary.totalRows === 1 ? 'row' : 'rows'} parsed`
    : `${createdPaymentCount} ${createdPaymentCount === 1 ? 'payment' : 'payments'}`;
  const metricCountLabel = rowSummary.hasRowResults ? 'Rows Parsed' : 'Payment Count';
  const metricCountValue = rowSummary.hasRowResults
    ? String(rowSummary.totalRows)
    : String(createdPaymentCount);
  const metricCountHelper = rowSummary.hasRowResults
    ? `${createdPaymentCount} created · ${rowSummary.failedRows} rejected`
    : undefined;
  const amountHelper =
    rowSummary.hasRowResults && rowSummary.parsedTotal > totalAmountNum
      ? `${formatCurrency(rowSummary.parsedTotal)} parsed from source rows`
      : undefined;
  const rejectedRowLabel = `${rowSummary.failedRows} ${
    rowSummary.failedRows === 1 ? 'row' : 'rows'
  }`;
  const validationTitle =
    fileStatus === FILE_STATUS.FAILED ? 'This file failed validation' : 'This file needs review';
  const validationDescription =
    rowSummary.failedRows > 0
      ? `${rejectedRowLabel} rejected before payment execution. Review the Reason column, correct the source file, then re-process.`
      : 'Review the rows below, correct any source file issues, then re-process when ready.';
  const paymentRecordsLabel = rowSummary.hasRowResults
    ? `${rowSummary.totalRows} rows · ${createdPaymentCount} payments`
    : `${createdPaymentCount} records`;

  return (
    <div className="flex flex-col gap-4 px-4 py-4 md:gap-5 md:py-6 lg:px-6">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
        <header className="flex flex-col gap-4">
          <nav className="text-xs text-muted-foreground">
            <Link to={breadcrumbOrigin.to} className="hover:text-foreground">
              {breadcrumbOrigin.label}
            </Link>
            <span className="mx-1.5">/</span>
            <span className="text-foreground">{file.file_name}</span>
          </nav>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="truncate text-2xl font-bold tracking-tight" title={file.file_name}>
                  {file.file_name}
                </h1>
                <Badge variant={statusVariant}>{statusLabel}</Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {file.client_name ? `${file.client_name} · ` : ''}
                uploaded {file.created_at ? formatDateLong(file.created_at) : 'date unknown'} ·{' '}
                {rowCountLabel}
              </p>
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {showDownloadButton && (
                <Button variant="outline" onClick={handleDownload}>
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
              )}
              {actionButtons.map((btn) => {
                const Icon = btn.icon;
                return (
                  <Button
                    key={btn.label}
                    disabled={isProcessing}
                    variant={btn.variant}
                    onClick={btn.onClick}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    {isProcessing ? btn.loadingLabel : btn.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </header>

        {isFailedLike && (
          <div
            className={`rounded-xl border p-4 ${
              validationTone === 'destructive'
                ? 'border-destructive/20 bg-destructive/10 text-destructive'
                : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200'
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="text-sm font-semibold">{validationTitle}</p>
                  <p className="mt-1 text-sm">{validationDescription}</p>
                </div>
              </div>
              {rowSummary.failedRows > 0 && (
                <Badge variant={validationTone === 'destructive' ? 'destructive' : 'secondary'}>
                  {rejectedRowLabel} rejected
                </Badge>
              )}
            </div>
          </div>
        )}

        <section className="grid gap-3 md:grid-cols-3">
          <DetailMetricCard
            icon={Wallet}
            label="Total Amount"
            value={formatCurrency(file.total_payment_amount)}
            helper={amountHelper}
            tone={isFailedLike ? validationTone : 'default'}
          />
          <DetailMetricCard
            icon={Receipt}
            label={metricCountLabel}
            value={metricCountValue}
            helper={metricCountHelper}
            tone={isFailedLike ? validationTone : 'default'}
          />
          <DetailMetricCard
            icon={Calculator}
            label="Avg. Amount"
            value={formatCurrency(avgAmount)}
            helper={createdPaymentCount ? 'Across created payments' : 'No created payments yet'}
            tone={isFailedLike ? validationTone : 'default'}
          />
        </section>

        <Card className="py-0">
          <CardContent className="p-0">
            <DetailSectionHeader icon={FileText} title="File Details" />
            <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
              <DetailInfoCell
                label="File ID"
                value={
                  <span className="font-mono" title={file.file_id}>
                    {file.file_id.slice(0, 8)}…
                  </span>
                }
              />
              <DetailInfoCell label="Client" value={file.client_name ?? '—'} />
              <DetailInfoCell label="Collection Type" value={file.collection_type ?? '—'} />
              <DetailInfoCell label="Debit Account" value={file.debit_account_identifier ?? '—'} />
              <DetailInfoCell label="Size" value={formatFileSize(file.file_size_bytes)} />
              <DetailInfoCell
                label="Uploaded"
                value={file.created_at ? formatDateLong(file.created_at) : '—'}
              />
              <DetailInfoCell
                label="Updated"
                value={file.updated_at ? formatDateLong(file.updated_at) : '—'}
              />
              <DetailInfoCell
                label="Source Rows"
                value={rowSummary.hasRowResults ? rowSummary.totalRows : '—'}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="py-0">
          <CardContent className="p-0">
            <DetailSectionHeader
              icon={Receipt}
              title="Payments in this file"
              meta={paymentRecordsLabel}
            />
            <PaymentFileDetailsTable
              sortedRowResults={sortedRowResults}
              payments={payments}
              paymentsError={paymentsError}
              isLoadingPayments={isLoadingPayments}
              paymentCount={createdPaymentCount}
              totalPaymentAmount={file.total_payment_amount}
              formatCurrency={formatCurrency}
              onPaymentClick={handlePaymentClick}
              externalRefLabel={externalRefLabel}
              hideBreakdown
              hideHeader
            />
          </CardContent>
        </Card>

        {payments.length > 0 && <PaymentMethodBreakdownCard payments={payments} />}

        <Card className="py-0">
          <CardContent className="p-0">
            <DetailSectionHeader icon={Calendar} title="Activity" />
            <div className="p-4">
              <p className="mb-3 text-xs text-muted-foreground">
                Upload, validation, and status history
              </p>
              <EventTimeline
                fetchEventLog={() => getFileEventLog(file.file_id)}
                title="File History"
                creationLabel="File Uploaded"
                embedded
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <PaymentDetails
        payment={selectedPayment}
        open={isPaymentDetailsOpen}
        onClose={() => {
          restoreFocus();
          setIsPaymentDetailsOpen(false);
        }}
        externalRefLabel={externalRefLabel}
      />
    </div>
  );
};
